import React from "react";
import CodeBlock from "../components/CodeBlock";

const AdvancedTutorial = () => (
  <section id="advanced" className="developer-section">
    <h2>Advanced Tutorial</h2>
    <p>
      This section covers streaming results, graph expansion, evidence chains,
      molecular search, and LLM chat integration.
    </p>

    <h3>Streaming Search with SSE</h3>
    <p>
      For large searches, enable Server-Sent Events (SSE) streaming to receive progressive
      updates instead of waiting for the full result. The <code>stream: true</code> query
      parameter is 9.x-only; 10.x replaces it with a dedicated <code>/sse</code> endpoint
      suffix — POST straight to <code>/api/search/sse</code> and the response is the stream
      itself (no separate <code>stream_id</code> + <code>GET /api/stream/&#123;id&#125;</code>{" "}
      round trip):
    </p>
    <CodeBlock
      title="Initiate a streaming search — 10.x: /search/sse"
      examples={{
        python: `import json, requests
from sseclient import SSEClient  # use the bundled sseclient.py

HEADERS = {"x-api-key": token, "content-type": "application/json"}

# One call: the response is the SSE stream (or inline JSON on a cache hit)
sse_resp = requests.post("/api/search/sse", headers={**HEADERS, "Accept": "text/event-stream"},
                         json={"ids": ["unichem:161671"]}, stream=True)
for event in SSEClient(sse_resp, events=["*"]).events():
    if event.event == "search_update":
        data = json.loads(event.data)
        print(f"Progress: {data.get('progress', 0)}% - {data.get('info', '')}")
    elif event.event == "search_results":
        results = json.loads(event.data)
        print(f"Results received! Categories: {list(results['totals'].keys())}")
        break
    elif event.event == "search_error":
        print(f"Error: {event.data}")
        break`,
        curl: `curl -N "/api/search/sse" \\
  -H "x-api-key: $PLEX_TOKEN" \\
  -H "Content-Type: application/json" \\
  -H "Accept: text/event-stream" \\
  -d '{"ids": ["unichem:161671"]}'`,
      }}
    />
    <CodeBlock
      title="Same thing on 9.x (or 10.x's two-step equivalent): stream=true + GET /stream/{id}"
      examples={{
        javascript: `// 1. Start streaming search (stream=true as query param)
const resp = await fetch("/api/search?stream=true", {
  method: "POST",
  headers: { "x-api-key": token, "Content-Type": "application/json" },
  body: JSON.stringify({ ids: ["unichem:161671"] })
});
const { stream_id } = await resp.json();

// 2. Connect and read the stream. /api/stream/{id} requires auth and
// EventSource can't send the x-api-key header, so use fetch instead.
const streamResp = await fetch(\`/api/stream/\${stream_id}\`, {
  headers: { "x-api-key": token, Accept: "text/event-stream" }
});
const reader = streamResp.body.getReader();
const decoder = new TextDecoder();
let buffer = "";
while (true) {
  const { done, value } = await reader.read();
  if (done) break;
  buffer += decoder.decode(value, { stream: true });
  let idx;
  while ((idx = buffer.indexOf("\\n\\n")) !== -1) {
    const block = buffer.slice(0, idx);
    buffer = buffer.slice(idx + 2);
    let type = "message", dataLines = [];
    for (const line of block.split("\\n")) {
      const [field, ...rest] = line.split(":");
      const value_ = rest.join(":").trimStart();
      if (field === "event") type = value_;
      else if (field === "data") dataLines.push(value_);
    }
    if (!dataLines.length) continue;
    const data = JSON.parse(dataLines.join("\\n"));
    if (type === "search_update") {
      console.log(\`Progress: \${data.progress}% - \${data.info}\`);
    } else if (type === "search_results") {
      console.log("Results:", Object.keys(data.totals));
    } else if (type === "search_error") {
      console.error("Error:", data.error);
    }
  }
}`,
        curl: `# 1. Start streaming search
STREAM_ID=$(curl -s "/api/search?stream=true" \\
  -H "x-api-key: $PLEX_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"ids": ["unichem:161671"]}' | jq -r '.stream_id')

# 2. Connect to SSE stream
curl -N "/api/stream/$STREAM_ID" \\
  -H "x-api-key: $PLEX_TOKEN" \\
  -H "Accept: text/event-stream"`,
      }}
    />
    <p>
      See the <a href="#sse-streaming">SSE Streaming</a> section for the complete event type
      reference and connection management details, including examples that read the full
      event stream, only the reconstituted messages, or only the final result.
      <code>search_results()</code> in{" "}
      <a href="/api_client.py" download>api_client.py</a> wraps this logic for all four
      transport modes (<code>stream</code>, <code>stream-poll</code>, <code>poll</code>,{" "}
      <code>sync</code>), trying 10.x's <code>/search/sse</code> first and falling back to
      9.x's <code>stream=true</code> two-step flow on a 404.
    </p>

    <h3>Graph Expansion &amp; Similarity Parameters</h3>
    <p>
      Control how the knowledge graph is traversed and how results are scored:
    </p>
    <table className="dev-api-params">
      <thead>
        <tr><th>Parameter</th><th>Type</th><th>Default</th><th>Description</th></tr>
      </thead>
      <tbody>
        <tr>
          <td><code>sim_type</code></td>
          <td>string</td>
          <td>"sim"</td>
          <td>Similarity algorithm: <code>sim</code>, <code>chem</code>, <code>ecfp2</code>–<code>ecfp8</code>, <code>maccs</code>, <code>pubchem</code>, <code>default</code>.</td>
        </tr>
        <tr>
          <td><code>sim_threshold</code></td>
          <td>float</td>
          <td>0.75</td>
          <td>Minimum similarity score (0–1). Lower values return more, weaker connections.</td>
        </tr>
        <tr>
          <td><code>sim_limit</code></td>
          <td>integer</td>
          <td>&mdash;</td>
          <td>Maximum number of similar items to include in expansion.</td>
        </tr>
        <tr>
          <td><code>sim_method</code></td>
          <td>string</td>
          <td>"tanimoto"</td>
          <td>Distance measure for chemical similarity: <code>tanimoto</code>, <code>tversky</code>, <code>euclid-sub</code>.</td>
        </tr>
        <tr>
          <td><code>sim_substructure</code></td>
          <td>boolean</td>
          <td>false</td>
          <td>Use substructure similarity search instead of full-molecule.</td>
        </tr>
        <tr>
          <td><code>limit_categories</code></td>
          <td>string[]</td>
          <td>&mdash;</td>
          <td>Restrict processing to these result category names.</td>
        </tr>
        <tr>
          <td><code>xf</code></td>
          <td>string</td>
          <td>&mdash;</td>
          <td>Expansion filter expression.</td>
        </tr>
        <tr>
          <td><code>min_expansion</code></td>
          <td>integer</td>
          <td>&mdash;</td>
          <td>Minimum entities to find in the first expansion (overrides sim threshold).</td>
        </tr>
        <tr>
          <td><code>find_related</code></td>
          <td>boolean</td>
          <td>false</td>
          <td>Include the seed category in results (pseudo-similarity search).</td>
        </tr>
        <tr>
          <td><code>indirect</code></td>
          <td>boolean</td>
          <td>false</td>
          <td>Experimental: include indirect connections in results.</td>
        </tr>
        <tr>
          <td><code>density_expansion</code></td>
          <td>boolean</td>
          <td>true</td>
          <td>Density-aware expansion for geneset/compound set comparisons.</td>
        </tr>
        <tr>
          <td><code>neighborhood</code></td>
          <td>string</td>
          <td>&mdash;</td>
          <td>Filter results to a specific community ID in the graph.</td>
        </tr>
        <tr>
          <td><code>imap</code></td>
          <td>integer (0&ndash;6)</td>
          <td>&mdash;</td>
          <td>Graph configuration index to use for expansion.</td>
        </tr>
        <tr>
          <td><code>direct_connection_id</code></td>
          <td>string</td>
          <td>&mdash;</td>
          <td>Filter results to entities with a direct connection to this entity ID.</td>
        </tr>
        <tr>
          <td><code>page_size</code></td>
          <td>integer</td>
          <td>&mdash;</td>
          <td>Number of results per page.</td>
        </tr>
        <tr>
          <td><code>full</code></td>
          <td>boolean</td>
          <td>true</td>
          <td>Include full entity metadata in results. Note: <code>entity</code> is present on
            results even when <code>full=false</code>.</td>
        </tr>
      </tbody>
    </table>

    <h3>Evidence Chains</h3>
    <p>
      For any search result, request the multi-hop evidence chain showing <em>how</em> your query
      entity connects to that result through intermediate nodes. Run the search with
      <code>find_related=true</code>, which brings results in the query's own category into
      scope, wait for it to finish, then POST its <code>search_id</code> and the
      <code>target_id</code> to <code>/api/evidence</code>. A <code>202</code> from
      <code>/api/evidence</code> means the search is still running: ask again.
    </p>
    <CodeBlock
      title="Fetch evidence chains"
      examples={{
        python: `import time

HEADERS = {"x-api-key": token, "Content-Type": "application/json"}

# 1. Run the search and wait for it to finish
def run_search():
    return requests.post("/api/search", params={"find_related": True}, headers=HEADERS,
                         json={"ids": ["unichem:161671"]}).json()
search = run_search()
while search["status"] != 200:
    time.sleep(2)
    search = run_search()

# 2. Ask for the chains to a target result, by the search's search_id
target_id = "entrezgene:1956"  # EGFR
chains = requests.post(
    "/api/evidence",
    headers=HEADERS,
    json={"search_id": search["search_id"], "target_id": target_id}
).json()
for chain in chains.get("paths", []):
    path_str = " -> ".join(node["entity"]["title"] for node in chain)
    print(f"  Path: {path_str}")`,
        javascript: `const headers = { "x-api-key": token, "Content-Type": "application/json" };

// 1. Run the search and wait for it to finish
const runSearch = () => fetch("/api/search?find_related=true", {
  method: "POST", headers,
  body: JSON.stringify({ ids: ["unichem:161671"] })
}).then(r => r.json());
let search = await runSearch();
while (search.status !== 200) {
  await new Promise(resolve => setTimeout(resolve, 2000));
  search = await runSearch();
}

// 2. Ask for the chains to a target result, by the search's search_id
const targetId = "entrezgene:1956"; // EGFR
const chains = await fetch("/api/evidence", {
  method: "POST", headers,
  body: JSON.stringify({ search_id: search.search_id, target_id: targetId })
}).then(r => r.json());
chains.paths?.forEach(chain => {
  const path = chain.map(n => n.entity.title).join(" -> ");
  console.log(\`  Path: \${path}\`);
});`,
        curl: `# 1. Run the search; re-run this until .status is 200
SEARCH_ID=$(curl -s "/api/search?find_related=true" \\
  -H "x-api-key: $PLEX_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"ids": ["unichem:161671"]}' | jq -r '.search_id')

# 2. Ask for the chains to a target result
curl -s /api/evidence \\
  -H "x-api-key: $PLEX_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d "{\\"search_id\\": \\"$SEARCH_ID\\", \\"target_id\\": \\"entrezgene:1956\\"}" \\
  | jq '.paths[] | [.[].entity.title] | join(" -> ")'`,
      }}
    />

    <h3>Molecular Search</h3>
    <p>
      Match a SMILES string against known compounds (<code>GET /api/molecule/match</code>, returns
      an SVG), or compare two molecules by SMILES (<code>POST /api/molecule/compare</code>):
    </p>
    <CodeBlock
      title="Molecular structure matching"
      examples={{
        python: `# Match a SMILES string to known compounds (SVG rendering, mol vs query)
ASPIRIN = "CC(=O)Oc1ccccc1C(=O)O"
resp = requests.get(
    "/api/molecule/match",
    headers={"x-api-key": token},
    params={"mol": ASPIRIN, "query": ASPIRIN}
)
with open("match.svg", "wb") as f:
    f.write(resp.content)

# Compare two molecules by SMILES
IBUPROFEN = "CC(C)Cc1ccc(cc1)C(C)C(=O)O"
resp = requests.post(
    "/api/molecule/compare",
    headers={"x-api-key": token, "Content-Type": "application/json"},
    json={"smiles1": ASPIRIN, "smiles2": IBUPROFEN}
)
comparison = resp.json()
print(f"Similarity: {comparison.get('similarity', 'N/A')}")`,
        curl: `# Match SMILES to compounds (GET, query params, returns SVG)
ASPIRIN="CC(=O)Oc1ccccc1C(=O)O"
curl -s -G /api/molecule/match \\
  -H "x-api-key: $PLEX_TOKEN" \\
  --data-urlencode "mol=$ASPIRIN" \\
  --data-urlencode "query=$ASPIRIN" \\
  -o match.svg

# Compare two molecules by SMILES
curl -s /api/molecule/compare \\
  -H "x-api-key: $PLEX_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"smiles1": "CC(=O)Oc1ccccc1C(=O)O", "smiles2": "CC(C)Cc1ccc(cc1)C(C)C(=O)O"}' | jq .`,
        javascript: `// Match SMILES to compounds (GET, query params, returns SVG)
const aspirin = "CC(=O)Oc1ccccc1C(=O)O";
const params = new URLSearchParams({ mol: aspirin, query: aspirin });
const svg = await fetch(\`/api/molecule/match?\${params}\`, {
  headers: { "x-api-key": token }
}).then(r => r.text());

// Compare two molecules by SMILES
const ibuprofen = "CC(C)Cc1ccc(cc1)C(C)C(=O)O";
const comparison = await fetch("/api/molecule/compare", {
  method: "POST",
  headers: {
    "x-api-key": token,
    "Content-Type": "application/json"
  },
  body: JSON.stringify({ smiles1: aspirin, smiles2: ibuprofen })
}).then(r => r.json());
console.log("Similarity:", comparison.similarity);`,
      }}
    />

    <h3>LLM Chat Integration</h3>
    <p>
      Send conversation messages to get AI-powered analysis, optionally grounded in a
      search's results. Chat has its own endpoint, <code>POST /api/chat</code> — it
      takes every chat parameter (<code>model</code>, <code>agent</code>,{" "}
      <code>history_id</code>, etc.) plus the standard search parameters for context.
      Same streaming split as search: on 10.x, POST straight to{" "}
      <code>/api/chat/sse</code> and the response is the stream itself; on 9.x, pass{" "}
      <code>stream=true</code> to <code>/api/chat</code> and connect to{" "}
      <code>/api/stream/&#123;stream_id&#125;</code> separately. Either way the event
      protocol is the same as search streaming:
    </p>
    <CodeBlock
      title="Chat with search context — 10.x: /chat/sse"
      examples={{
        python: `import json, requests
from sseclient import SSEClient  # use the bundled sseclient.py

HEADERS = {"x-api-key": token, "content-type": "application/json"}

# One call: the response is the SSE stream
sse_resp = requests.post(
    "/api/chat/sse",
    headers={**HEADERS, "Accept": "text/event-stream"},
    json={
        "ids": ["unichem:161671"],
        "messages": [{"role": "user",
                      "content": [{"text": "What are the main therapeutic targets of aspirin?"}]}]
    },
    stream=True,
)
answer = ""
for event in SSEClient(sse_resp, events=["*"]).events():
    if event.event == "content_block_delta":
        text = json.loads(event.data).get("delta", {}).get("text", "")
        answer += text
        print(text, end="", flush=True)
    elif event.event == "message_stop":
        break
print()`,
        curl: `curl -N "/api/chat/sse" \\
  -H "x-api-key: $PLEX_TOKEN" \\
  -H "Content-Type: application/json" \\
  -H "Accept: text/event-stream" \\
  -d '{
    "ids": ["unichem:161671"],
    "messages": [{"role": "user", "content": [{"text": "What are the main targets of aspirin?"}]}]
  }'`,
      }}
    />
    <CodeBlock
      title="Same thing on 9.x (or 10.x's two-step equivalent): stream=true + GET /stream/{id}"
      examples={{
        javascript: `// Send chat with search context (stream=true as query param)
const resp = await fetch("/api/chat?stream=true", {
  method: "POST",
  headers: { "x-api-key": token, "Content-Type": "application/json" },
  body: JSON.stringify({
    ids: ["unichem:161671"],
    messages: [{
      role: "user",
      content: [{ text: "What are the main therapeutic targets of aspirin?" }]
    }]
  })
});
const { stream_id } = await resp.json();

// Connect and read the stream. /api/stream/{id} requires auth and
// EventSource can't send the x-api-key header, so use fetch instead.
const streamResp = await fetch(\`/api/stream/\${stream_id}\`, {
  headers: { "x-api-key": token, Accept: "text/event-stream" }
});
const reader = streamResp.body.getReader();
const decoder = new TextDecoder();
let buffer = "", answer = "";
while (true) {
  const { done, value } = await reader.read();
  if (done) break;
  buffer += decoder.decode(value, { stream: true });
  let idx;
  while ((idx = buffer.indexOf("\\n\\n")) !== -1) {
    const block = buffer.slice(0, idx);
    buffer = buffer.slice(idx + 2);
    let type = "message", dataLines = [];
    for (const line of block.split("\\n")) {
      const [field, ...rest] = line.split(":");
      const value_ = rest.join(":").trimStart();
      if (field === "event") type = value_;
      else if (field === "data") dataLines.push(value_);
    }
    if (!dataLines.length) continue;
    const data = JSON.parse(dataLines.join("\\n"));
    if (type === "content_block_delta") {
      const text = data?.delta?.text || "";
      answer += text;
      process.stdout.write(text);
    } else if (type === "message_stop") {
      console.log("\\n--- Done ---");
    }
  }
}`,
        curl: `# Start chat stream
STREAM_ID=$(curl -s "/api/chat?stream=true" \\
  -H "x-api-key: $PLEX_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "ids": ["unichem:161671"],
    "messages": [{"role": "user", "content": [{"text": "What are the main targets of aspirin?"}]}]
  }' | jq -r '.stream_id')

# Stream the response
curl -N "/api/stream/$STREAM_ID" \\
  -H "x-api-key: $PLEX_TOKEN" \\
  -H "Accept: text/event-stream"`,
      }}
    />
    <p>
      <code>agent_results()</code> in{" "}
      <a href="/api_client.py" download>api_client.py</a> wraps this flow (trying{" "}
      <code>/chat/sse</code> first, falling back to the <code>stream=true</code> two-step
      flow on a 404, plus the <code>stream-poll</code> fallback for proxies that block{" "}
      <code>text/event-stream</code>) and yields parsed <code>&#123;type, data&#125;</code>{" "}
      events one at a time.
    </p>
    <p>
      <code>POST /api/search</code> with a <code>messages</code> body still works too —
      it is not replaced by <code>/api/chat</code>, just no longer the only way in. Existing
      integrations built on <code>/api/search</code> + <code>messages</code> do not need to
      migrate.
    </p>

  </section>
);

export default AdvancedTutorial;
